import React from 'react';
import Card from '../Card';
import SearchableSelect from '../SearchableSelect';
import { HBarChart, phasesOn } from '../charts';
import PercentileChart, { latencyHasSamples } from './PercentileChart';

/* ── Tools: how long tool calls and whole turns took, then what was called ──
   The Dashboard's section (what each card counts is explained there, above its use),
   and the load tests' report draws the same cards for a phase (pages/LoadTests.jsx) from
   the run's own events (backend loadtest/dashboard_views.py). `formatX`, `timeline` (the
   run's phases, charts/phases.jsx) and `gapOnNull` are for the report. The tool picker
   is controlled: the Dashboard refetches the card when it changes. */

/* Turn latency, on its own for the report's Messages phase, which has turns but no
   tools to show. */
export function TurnLatencyCard({ data, formatX, timeline, gapOnNull = false, span = 6 }) {
  return (
    <Card
      title="Turn latency"
      subtitle="user message to the last agent reply before their next message"
      help="One point per turn, not per message: for U1 A1 A2 A3 A4 U5 this is U1 to A4, bucketed on the question. Turns over an hour are discarded, not clamped — message_logs cannot tell a reply from a cron, so one firing before the user returns would become the turn's last reply and absorb the silence. Long workflow turns go too, so read this as a floor on the slowest work. The Source filter does not apply."
      span={span}
      empty={latencyHasSamples(data) ? null : 'No completed turns in this window'}>
      <PercentileChart data={data} formatX={formatX} phases={phasesOn(data, timeline)}
                       gapOnNull={gapOnNull} />
    </Card>
  );
}

export default function ToolsSection({
  toolLatency, tool, onTool, turnLatency, topTools, platformTools,
  platformToolsIgnored = false, error, formatX, timeline, gapOnNull = false,
}) {
  const latency = toolLatency || {};
  const top = topTools || [];
  const ran = platformTools || [];
  return (
    <>
      <div className="section-head">
        <h2>Tools</h2>
        <p>how long tool calls and whole turns took, then what was called</p>
      </div>

      {error && (
        <div className="notice notice-danger" role="status">
          <div className="notice-body">
            Could not load: {error}. The charts below may be incomplete; the rest
            of this page is unaffected.
          </div>
        </div>
      )}

      <div className="dashboard-grid">
        {/* Tool latency — the first chart in this product with its own on-graph filter. */}
        <Card
          title="Tool call latency"
          subtitle={tool
            ? `proxy-tools · ${tool}`
            : 'proxy-tools (MCP) · all tools'}
          help="Successful invocations only. proxy-tools also audits its three rejection paths — ACL denial, rate limit, invalid arguments — with a duration near zero in the same column, and counting those would make the chart look BETTER the more calls were refused. A tool call has no model, so the page's model filter does not apply to this card."
          span={6}
          empty={latencyHasSamples(latency.data) ? null : 'No tool calls recorded in this window'}
          actions={
            /* The tool selector. `tools` lists only what has samples in the current
               window, computed with this filter REMOVED, so picking a tool never
               collapses the list to that one tool. */
            <SearchableSelect
              value={tool}
              onChange={onTool}
              placeholder="All tools"
              searchPlaceholder="Search tools…"
              aria-label="Tool shown on the tool latency chart"
              options={(latency.tools || []).map(t => ({ value: t, label: t }))}
              minWidth={170}
            />
          }>
          {latency.model_ignored && (
            <p className="text-sm text-muted mt-0 mb-2">
              The model filter does not apply here — a tool call has no model, so this card
              shows every model.
            </p>
          )}
          <PercentileChart data={latency.data} formatX={formatX}
                           phases={phasesOn(latency.data, timeline)} gapOnNull={gapOnNull} />
        </Card>

        {/* Turn latency — user message to the last agent reply before their next message. */}
        <TurnLatencyCard data={turnLatency?.data} formatX={formatX} timeline={timeline}
                         gapOnNull={gapOnNull} />

        <Card title="Top tools"
              subtitle={`${top.reduce((s, t) => s + t.count, 0).toLocaleString()} calls made by the agent · platform tools counted on the right`}
              span={6}
              empty={top.length === 0 ? 'No tool calls yet' : null}>
          <HBarChart data={top} />
        </Card>
        <Card title="Platform tools run"
              subtitle={platformToolsIgnored
                ? 'not shown: a model filter cannot narrow these'
                : `${ran.reduce((s, t) => s + t.count, 0).toLocaleString()} invocations at proxy-tools · shorter history`}
              span={6}
              empty={platformToolsIgnored
                ? 'Clear the model filter to see platform-tool calls'
                : ran.length === 0 ? 'No platform-tool calls yet' : null}>
          <HBarChart
            data={ran}
            nameWidth={150}
            /* Teal, deliberately not the default purple: these bars are a different
               measurement from the ones on the left and should not read as the same series
               split in two. */
            colorFn={() => 'linear-gradient(90deg, var(--series-2), var(--series-11))'}
          />
        </Card>
      </div>
    </>
  );
}
